import storage from '../module/storage.js'

const getStoredTheme = () => storage.getItem('theme')
const setStoredTheme = (theme) => storage.setItem('theme', theme)

const getPreferredTheme = () => {
  const storedTheme = getStoredTheme()

  if (storedTheme) {
    return storedTheme
  }

  // Set default theme to 'light'.
  // Possible options: 'dark' or system color mode (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
  return 'light'
}

const setTheme = (theme) => {
  if (theme === 'auto') {
    theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }

  document.documentElement.setAttribute('data-bs-theme', theme)

  document.querySelectorAll('etrusted-widget').forEach((widget) => {
    widget.setAttribute('data-theme', theme)
  })
}

setTheme(getPreferredTheme())

// eTrusted (Trusted Shops) widgets follow the visitor's OS colour scheme unless they carry `data-theme`, and they ignore
// the site's `data-bs-theme`. Their app watches `data-theme` on the <etrusted-widget> element, so setting it there also
// switches a widget that has already rendered. Widgets can arrive after page load (cart and checkout summaries, product
// reviews in search results), so give those the current theme as soon as they are added to the page.
new MutationObserver((mutations) => {
  const theme = document.documentElement.getAttribute('data-bs-theme')

  mutations.forEach(({ addedNodes }) => {
    addedNodes.forEach((node) => {
      if (!(node instanceof Element)) {
        return
      }

      const widgets = node.matches('etrusted-widget') ? [node] : node.querySelectorAll('etrusted-widget')

      widgets.forEach((widget) => {
        if (widget.getAttribute('data-theme') !== theme) {
          widget.setAttribute('data-theme', theme)
        }
      })
    })
  })
}).observe(document.documentElement, { childList: true, subtree: true })

// There are two switchers: the dropdown in the header on desktop, and the buttons in the menu below the lg breakpoint.
const showActiveTheme = (theme) => {
  document.querySelectorAll('[data-bs-theme-value]').forEach((element) => {
    const isActive = element.getAttribute('data-bs-theme-value') === theme

    element.classList.toggle('active', isActive)
    element.setAttribute('aria-pressed', String(isActive))
  })

  const activeIcon = document.querySelector(`[data-bs-theme-value="${theme}"] .theme-icon i`)

  if (!activeIcon) {
    return
  }

  document.querySelectorAll('.theme-icon-active i').forEach((icon) => {
    icon.className = activeIcon.className
  })
}

window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
  const storedTheme = getStoredTheme()
  if (storedTheme !== 'light' && storedTheme !== 'dark') {
    setTheme(getPreferredTheme())
  }
})

window.addEventListener('DOMContentLoaded', () => {
  showActiveTheme(getPreferredTheme())

  document.querySelectorAll('[data-bs-theme-value]').forEach((toggle) => {
    toggle.addEventListener('click', () => {
      const theme = toggle.getAttribute('data-bs-theme-value')
      setStoredTheme(theme)
      setTheme(theme)
      showActiveTheme(theme)

      // The header dropdown closes on click, so give focus back to its toggle button
      toggle.closest('.dropdown')?.querySelector('.theme-switcher')?.focus()
    })
  })
})

export { getStoredTheme, getPreferredTheme }
